/** Edit dialog for level=tag flag rows — the deliberate mutability exception.
 *
 * Other levels are enabled/comment-only after creation, but with no DELETE
 * and one tag row per feature an immutable wrong tag set would be a dead end,
 * so tag rows allow PATCHing `tags` (wholesale replace) / `tag_match_mode` /
 * `tag_sources`. `enabled` is deliberately NOT here — the grid's inline
 * Switch owns it (form-dialog pattern: plain useState, seeded from the row).
 */
import { Box, Button, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as nffApi from '@/api/notificationFeatures';
import type {
  FeatureFlagListItem,
  FlagChannel,
  FlagPolarity,
  TagSourceDef,
} from '@/api/notificationFeatures';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { FieldHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { TagConditionInput, type TagConditionValue } from '@/components/TagConditionInput';

import { FLAG_HELP } from './fieldHelp';
import { TagFlagScopeFields } from './TagFlagScopeFields';
import { tagConditionFetchers } from './tagConditionFetchers';
import { fromFlagRow, toCreateTags } from './tagFlagUtils';
import { TagSourcesSelect } from './TagSourcesSelect';

interface Props {
  open: boolean;
  row: FeatureFlagListItem;
  /** Registry tag sources (options for the sources select). */
  tagSources: TagSourceDef[];
  onClose: () => void;
}

export function EditTagFlagDialog({ open, row, tagSources, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const history = useEditorHistoryTabs({
    enabled: hasPerm('console_audit_view'),
    contentType: 'demo.notification_feature_flag',
    objectPk: row.id,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['notification-feature-flags'] }),
  });

  const [condition, setCondition] = useState<TagConditionValue>(() => fromFlagRow(row));
  const [sources, setSources] = useState<string[]>(() => row.tag_sources ?? []);
  const [comment, setComment] = useState(row.comment ?? '');
  const [polarity, setPolarity] = useState<FlagPolarity>(row.polarity ?? 'require');
  const [channel, setChannel] = useState<FlagChannel>(row.channel ?? '');

  const updateMutation = useMutation({
    mutationFn: () =>
      nffApi.update(row.id, {
        tags: toCreateTags(condition),
        tag_match_mode: condition.matchMode,
        tag_sources: sources,
        polarity,
        channel,
        comment: comment.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-feature-flags'] });
      snackbar.show('Tag condition updated.', 'success');
      onClose();
    },
    onError: (e) =>
      snackbar.show(
        e instanceof ConsoleApiError ? e.message : 'Could not update the tag condition.',
        'error',
      ),
  });

  const canSubmit = condition.chips.length > 0 && sources.length > 0 && !updateMutation.isPending;

  const hasPinnedChip = condition.chips.some((c) => 'tag_id' in c.spec);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={
        <>
          Edit tag condition — {row.feature} <HelpLink story="notification-feature-flags" newTab />
        </>
      }
      size="md"
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={() => updateMutation.mutate()}
            >
              Save
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <TagConditionInput
                value={condition}
                onChange={setCondition}
                fetchers={tagConditionFetchers}
                label="Tag condition"
                helperText="Replaces the stored chip set wholesale on save."
              />
            </Box>
            <FieldHelp help={FLAG_HELP.tag_condition} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TagSourcesSelect value={sources} onChange={setSources} options={tagSources} />
            <FieldHelp help={FLAG_HELP.tag_sources} />
          </Box>
          <TagFlagScopeFields
            polarity={polarity}
            onPolarityChange={setPolarity}
            channel={channel}
            onChannelChange={setChannel}
            // The row's own operator: a tag row's tour_operator is its scope, and
            // scope is immutable after create on every level — moving a compiled
            // condition between operators would silently re-point live
            // suppression, and creating the row for the other operator is one click.
            tenant={
              row.tenant
                ? { id: row.tenant.id, name: row.tenant.name, code: row.tenant.code }
                : null
            }
            onTenantChange={() => {}}
            scopeLocked
            chipCount={condition.chips.length}
          />
          {hasPinnedChip && (
            <Typography variant="caption" color="info.main">
              A tenant-pinned tag (#) never matches renders of other tenants — in AND mode it makes
              the feature unrenderable everywhere else. Use a system-wide chip or OR mode if that is
              not intended.
            </Typography>
          )}
          <TextField
            size="small"
            label="Comment (optional)"
            multiline
            minRows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            InputProps={{ endAdornment: <FieldHelp help={FLAG_HELP.comment} /> }}
          />
        </Box>
      )}
    </AppDialog>
  );
}
